import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "Products",
      questions: [
        {
          q: "Are refurbished laptops reliable?",
          a: "Yes! All our refurbished laptops undergo rigorous testing and quality checks. They come with warranty coverage and are certified to perform like new."
        },
        {
          q: "What's the difference between refurbished and new?",
          a: "Refurbished laptops are pre-owned units that have been professionally restored to excellent condition. New laptops are brand new from manufacturers. Both come with warranty protection."
        },
        {
          q: "Do you sell genuine products?",
          a: "Absolutely. We source all products from authorized distributors and manufacturers. Every item comes with official warranty and authenticity guarantee."
        },
        {
          q: "Can I upgrade my laptop later?",
          a: "Many of our laptops support upgrades for RAM and storage. Contact our support team for specific model compatibility."
        }
      ]
    },
    {
      category: "Shipping & Delivery",
      questions: [
        {
          q: "How long does delivery take?",
          a: "Nairobi: Same-day or next-day delivery. Other major cities: 1-3 business days. Remote areas: 3-5 business days."
        },
        {
          q: "Do you offer free shipping?",
          a: "Free shipping on orders above KSh 50,000 within major cities. Standard shipping rates apply for other orders."
        },
        {
          q: "Can I pick up from your store?",
          a: "Yes! We have pickup locations in Nairobi, Meru Town, and Mombasa. Select 'Store Pickup' during checkout."
        },
        {
          q: "How do I track my order?",
          a: "Once shipped, you'll receive a tracking number via email and SMS. You can also track orders in your dashboard."
        }
      ]
    },
    {
      category: "Returns & Warranty",
      questions: [
        {
          q: "What is your return policy?",
          a: "14-day return policy for unused items in original packaging. Refurbished items: 7-day return window. Contact us to initiate a return."
        },
        {
          q: "How long is the warranty?",
          a: "New products: 1-3 years manufacturer warranty. Refurbished products: 3-6 months warranty. Extended warranty options available."
        },
        {
          q: "What if my product is defective?",
          a: "Contact us immediately. We'll arrange a replacement or repair at no cost if the defect is covered under warranty."
        },
        {
          q: "Can I exchange a product?",
          a: "Yes, within the return period. The replacement item must be of equal or greater value."
        }
      ]
    },
    {
      category: "Payment",
      questions: [
        {
          q: "What payment methods do you accept?",
          a: "M-Pesa (STK Push, Till, Paybill), Credit/Debit Cards (Visa, Mastercard), and Bank Transfers."
        },
        {
          q: "Is it safe to pay online?",
          a: "Yes. We use industry-standard encryption and secure payment gateways to protect your information."
        },
        {
          q: "Do you offer installment plans?",
          a: "Yes, we partner with select financial providers for installment payment options on orders above KSh 30,000."
        },
        {
          q: "When will I be charged?",
          a: "Payment is processed immediately upon order confirmation. For M-Pesa, you'll receive an STK push prompt."
        }
      ]
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  let globalIndex = 0;

  return (
    <Layout>
      <div style={{ background: 'var(--color-background)' }}>
        {/* Hero Section */}
        <section className="section-padding" style={{ background: 'var(--color-background-secondary)' }}>
          <div className="container-custom text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
              Frequently Asked Questions
            </h1>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Find answers to common questions about our products, shipping, returns, and more.
            </p>
          </div>
        </section>

        {/* FAQ Sections */}
        <section className="section-padding">
          <div className="container-custom max-w-4xl">
            {faqs.map((section, sectionIdx) => (
              <div key={sectionIdx} className="mb-12">
                <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
                  {section.category}
                </h2>
                <div className="space-y-4">
                  {section.questions.map((faq, qIdx) => {
                    const currentIndex = globalIndex++;
                    const isOpen = openIndex === currentIndex;
                    return (
                      <Card key={qIdx} className="luxury-card">
                        <CardContent className="p-0">
                          <button
                            onClick={() => toggleFAQ(currentIndex)}
                            className="w-full p-6 text-left flex items-center justify-between"
                            data-testid={`faq-${currentIndex}`}
                          >
                            <h3 className="text-lg font-semibold pr-4" style={{ color: 'var(--color-text-primary)' }}>
                              {faq.q}
                            </h3>
                            {isOpen ? (
                              <ChevronUp size={24} style={{ color: 'var(--color-accent)' }} />
                            ) : (
                              <ChevronDown size={24} style={{ color: 'var(--color-text-muted)' }} />
                            )}
                          </button>
                          {isOpen && (
                            <div className="px-6 pb-6">
                              <p style={{ color: 'var(--color-text-secondary)' }}>{faq.a}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="section-padding" style={{ background: 'var(--color-background-secondary)' }}>
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
              Still Have Questions?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Our support team is ready to help you with any questions or concerns.
            </p>
            <a href="/contact">
              <button className="luxury-button-primary" data-testid="contact-us-button">
                Contact Us
              </button>
            </a>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default FAQPage;